/**
 * editTypeVocabulary — the `AgentFeedback.edit_type` scale, in one place.
 *
 * `edit_type` is a machine token that names which part of an AI output a technician changed, or that
 * they rated the run. Every projection of an `agent_feedback` row carries it verbatim and no producer
 * ever writes Hebrew into it. Three surfaces print it to a technician, and each held its own map:
 * `FeedbackPage` (thirteen keys), `AgentRunsDashboardPage` (five, abbreviated for a 9px chip) and
 * `AgentRunDetailPage` — which held a five-key map for its correction-type `<select>` and then
 * printed the field itself in the correction history row, so the run's own audit card read
 * `תיקון analysis_edit`. The map was one clause away from the line that needed it.
 *
 * The key set below is the writable set and nothing else — every token some code path can store,
 * re-derived from the producer sources by `editTypeVocabulary.test.js`:
 *
 * 1. The five correction types. Declared three times over on the backend and agreeing in all three:
 *    `models/agent_feedback.py`'s own "Edit types" docstring and its `edit_type` column comment,
 *    and `schemas/agent_run.py`'s `_VALID_EDIT_TYPES` frozenset, which is what the correction POST
 *    validates against before `routers/agent_runs.py` inserts the row.
 * 2. `quality_rating` — the row `POST /agent-runs/{run_id}/rate` writes for a thumbs-up/down click
 *    (`routers/agent_runs.py`, the only other insert site in the tree). It is *not* a correction:
 *    the same module excludes it by name from every "edits"/"corrections" aggregate
 *    (`_REAL_EDIT_TYPE`), because a satisfaction click counted as a rewrite would misreport quality.
 *    It still appears in `GET /agent-runs/{run_id}/feedback`, which filters nothing, so it is a row
 *    the history card renders.
 *
 * Deliberately NOT keys: `approve`, `reject`, `approve_with_edit`, `priority_change`, `action_change`,
 * `summary_edit` and `field_correction`, which `FeedbackPage` carries. Those are that page's *filter*
 * options — a scale a human may query by — and no writer in this tree mints any of them onto a row.
 * A key the producer cannot mint is the fault this vocabulary exists to prevent: it lets a fixture
 * written against the map pass while the real token falls through to the raw identifier
 * (`systemTypeVocabulary` carries the same rule, and the same paragraph).
 */

export const EDIT_TYPE_LABEL = {
  // ── The five correction types ───────────────────────────────────────────────
  // The wording `AgentRunDetailPage`'s correction-type select has always shown.
  analysis_edit: "תיקון ניתוח",
  reply_edit: "תיקון תגובה ללקוח",
  action_edit: "תיקון פעולה",
  closing_edit: "תיקון מייל סגירה",
  question_edit: "תיקון שאלה",

  // ── The rating row ──────────────────────────────────────────────────────────
  // Not a correction, and deliberately not worded as one: no `תיקון` prefix, because this row is a
  // thumbs-up/down click on the run. `FeedbackPage` already names this token with these two words,
  // and both surfaces must keep saying the same thing about it.
  quality_rating: "דירוג איכות",
};

/**
 * The edit types a technician may choose when saving a correction — the same five `_VALID_EDIT_TYPES`
 * accepts, in the order the select has always listed them.
 *
 * `quality_rating` must NOT be offered here even though it has a label above: a correction submitted
 * under that token would be excluded by `_REAL_EDIT_TYPE` from every correction statistic, so the
 * technician's edit would be stored and then not counted as an edit anywhere.
 */
export const CORRECTION_EDIT_TYPES = [
  "analysis_edit",
  "reply_edit",
  "action_edit",
  "closing_edit",
  "question_edit",
];

/**
 * The Hebrew name of one edit type, or the token itself when this build has no name for it.
 *
 * Returning the token is deliberate: an unmapped token is a token nobody has swept yet, and showing
 * the operator the producer's own identifier is the honest failure — inventing a word for a type the
 * vocabulary does not describe would be a guess presented as a label.
 */
export function editTypeLabel(value) {
  if (typeof value !== "string" || !value) return null;
  return EDIT_TYPE_LABEL[value] ?? value;
}
